import { useState, useEffect } from "react";
import { Clock, CheckCircle2, Loader2, MessageCircle, X } from "lucide-react";
import { PageLoader } from "@/components/ui/page-loader";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
	getSlotsInRange,
	createPreparationBooking,
	formatSlotTime,
} from "../services/integrations/cal";
import { formatDateString } from "../services/integrations/cal/calDateHelpers";
import { CalSlot } from "../types/scheduling";
import { showIntercomWithMessage } from "../services/integrations/intercom";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { getCurrentUser } from "@/services/data/users";
import { getProductPreparationDuration } from "@/services/data/products";
import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger";

// Format une tranche horaire (début - fin)
const formatTimeRange = (start: string, end: string): string => {
	return `${formatSlotTime(start)} - ${formatSlotTime(end)}`;
};

interface PreparationBookingScreenProps {
	open: boolean;
	onClose: () => void;
	shootingDate: Date;
	userName: string;
	userEmail: string;
	productId: string;
	shootingId?: string | null;
	onBookingSuccess: (preparationSlot: string) => void;
}

export function PreparationBookingScreen({
	open,
	onClose,
	shootingDate,
	userName,
	userEmail,
	productId,
	shootingId,
	onBookingSuccess,
}: PreparationBookingScreenProps) {
	const [availableDates, setAvailableDates] = useState<Date[]>([]);
	const [dateSlots, setDateSlots] = useState<Map<string, CalSlot[]>>(new Map());
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [selectedSlot, setSelectedSlot] = useState<CalSlot | undefined>(
		undefined,
	);
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
	const [isBooking, setIsBooking] = useState<boolean>(false);
	const [preparationDuration, setPreparationDuration] = useState<number>(45);

	// Récupérer la durée de préparation depuis la DB
	useEffect(() => {
		const fetchPreparationDuration = async () => {
			if (!productId) return;

			const result = await getProductPreparationDuration(productId);

			if (result.success && result.data?.preparation_duration_minutes) {
				setPreparationDuration(result.data.preparation_duration_minutes);
			}
		};

		fetchPreparationDuration();
	}, [productId]);

	// Calculer les 15 jours avant la date de tournage et charger tous les créneaux en une fois
	useEffect(() => {
		const loadAvailabilities = async () => {
			if (!productId) return;

			setIsLoading(true);

			logger.debug("[PreparationBooking] Loading availabilities", { shootingDate });

			const userResult = await getCurrentUser();

			if (!userResult.success || !userResult.data) {
				setIsLoading(false);
				return;
			}

			const user = userResult.data;

			// Générer les 15 jours précédant le tournage (J-15 à J-1)
			const dates: Date[] = [];
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			for (let i = 15; i >= 1; i--) {
				const date = new Date(shootingDate);
				date.setDate(date.getDate() - i);
				date.setHours(0, 0, 0, 0);
				if (date >= today) dates.push(date);
			}

			if (dates.length === 0) {
				logger.debug("[PreparationBooking] No available dates (all in the past)");
				setAvailableDates([]);
				setDateSlots(new Map());
				setIsLoading(false);
				return;
			}

			logger.debug(`[PreparationBooking] ${dates.length} dates available`, dates.map(d => d.toLocaleDateString("fr-FR")));

			setAvailableDates(dates);

			// Calculer la plage de dates (du premier jour au dernier jour) en timezone-safe
			const startDate = new Date(dates[0]);
			startDate.setHours(12, 0, 0, 0);
			const endDate = new Date(dates[dates.length - 1]);
			endDate.setHours(12, 0, 0, 0);

			logger.debug("[PreparationBooking] Fetching slots range", { from: startDate.toISOString(), to: endDate.toISOString() });

			try {
				// Charger tous les créneaux pour la plage en une seule requête
				const slots = await getSlotsInRange(
					startDate,
					endDate,
					user.id,
					productId,
					undefined, // locationId not needed for preparation
					"preparation", // eventType
				);

				logger.debug(`[PreparationBooking] ${slots.length} slots retrieved`);

				// Organiser les créneaux par date (timezone-safe)
				const slotsMap = new Map<string, CalSlot[]>();
				slots.forEach((slot) => {
					const slotDate = new Date(slot.start);
					const dateStr = formatDateString(slotDate);
					const current = slotsMap.get(dateStr) || [];
					current.push(slot);
					slotsMap.set(dateStr, current);
				});

				logger.debug(`[PreparationBooking] ${slotsMap.size} dates with available slots`);
				setDateSlots(slotsMap);
			} catch (error) {
				logger.error("[PreparationBooking] Error loading slots:", error);
				toast.error("Une erreur est survenue lors du chargement des créneaux");
				setDateSlots(new Map());
			} finally {
				setIsLoading(false);
			}
		};

		loadAvailabilities();
	}, [shootingDate, productId]);

	const handleSlotSelect = (slot: CalSlot, date: Date) => {
		setSelectedSlot(slot);
		setSelectedDate(date);
	};

	const confirmBookingInternal = async () => {
		if (!selectedSlot || !selectedDate) {
			toast.error("Veuillez sélectionner un créneau");
			return;
		}

		setIsBooking(true);

		const userResult = await getCurrentUser();

		if (!userResult.success || !userResult.data || !productId) {
			toast.error("Erreur de configuration");
			setIsBooking(false);
			return;
		}

		const user = userResult.data;

		const response = await createPreparationBooking(
			selectedSlot.start,
			userName,
			userEmail,
			shootingDate.toISOString(),
			user.id,
			productId,
			shootingId || null,
		);

		setIsBooking(false);

		if (response.success) {
			// Notifier le parent du succès
			onBookingSuccess(selectedSlot.start);
		} else {
			toast.error("Erreur lors de la réservation", {
				description: response.error || "Veuillez réessayer",
			});
		}
	};
	const [handleConfirmBooking] = useDebouncedCallback(
		confirmBookingInternal,
		500,
	);

	const formatDateHeader = (date: Date): string => {
		const options: Intl.DateTimeFormatOptions = {
			weekday: "short",
			day: "numeric",
			month: "short",
		};
		return date.toLocaleDateString("fr-FR", options);
	};

	const getDateKey = (date: Date): string => {
		return formatDateString(date);
	};

	const handleContactConcierge = () => {
		const message = `Bonjour,\n\nJe souhaiterais planifier mon RDV de préparation pour mon tournage du ${shootingDate.toLocaleDateString("fr-FR")}.\n\nMalheureusement, je ne trouve pas de créneaux disponibles dans les 15 jours précédant cette date.\n\nPouvez-vous m'aider à organiser ce RDV ?\n\nMerci !`;

		showIntercomWithMessage(message);
	};

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className=" max-w-full w-full h-full p-0 border-0 bg-black z-[60] flex flex-col pt-[env(safe-area-inset-top)]">
				{/* Header glass fixe */}
				<div className="sticky top-0 z-20 glass border-b border-white/10 px-6 py-4">
					<div className="flex items-start justify-between">
						<div className="flex-1">
							<div className="flex items-center gap-3 mb-2">
								<CheckCircle2 className="h-6 w-6 text-green-400" />
								<p className="text-white text-base">
									Tournage confirmé le{" "}
									<span className="font-semibold">
										{shootingDate.toLocaleDateString("fr-FR", {
											day: "numeric",
											month: "long",
											year: "numeric",
										})}
									</span>
								</p>
							</div>
						</div>
						<button
							onClick={onClose}
							className="glass border border-white/20 rounded-full p-2 text-white hover:bg-white/10 active:scale-95 transition-all ml-4"
						>
							<X className="h-5 w-5" />
						</button>
					</div>
				</div>
				{/* Contenu scrollable */}
				<div className="flex-1 overflow-y-auto pb-32 pt-4 scrollbar-hide">
					<div className="p-6 space-y-6">
						{isLoading ? (
							<PageLoader
								variant="section"
								message="Chargement des créneaux..."
							/>
						) : dateSlots.size === 0 ? (
							<div className="glass rounded-2xl p-8 text-center space-y-6">
								<h2 className="text-xl text-white font-semibold">
									Réservez votre RDV éditorial
								</h2>

								<p className="text-white/60 text-sm max-w-md mx-auto">
									Co-créons votre ligne éditoriale et définissons les
									thématiques de vos vidéos pour maximiser leur impact.
								</p>

								<div className="flex items-center justify-center gap-2">
									<Clock className="h-4 w-4 text-white" />
									<span className="text-white/70 text-xs">
										{preparationDuration} minutes • Dans les 15 jours avant le
										tournage
									</span>
								</div>

								<Separator className="bg-white/10" />

								<div className="space-y-3">
									<p className="text-white text-base">
										Aucun créneau disponible
									</p>
									<p className="text-white/60 text-xs max-w-md mx-auto">
										Tous les créneaux sont déjà réservés pour les 15 jours
										précédant votre tournage.
									</p>
								</div>

								<button
									onClick={handleContactConcierge}
									className="bg-white/10 hover:bg-white/15 active:scale-95 text-white rounded-2xl px-8 py-4 shadow-xl transition-all inline-flex items-center gap-3"
								>
									<MessageCircle className="h-5 w-5" />
									<span>Solliciter la conciergerie</span>
								</button>

								<p className="text-white/40 text-xs">
									Notre équipe vous aidera à trouver un créneau adapté
								</p>
							</div>
						) : (
							<>
								{/* Bloc intro */}
								<div className="glass rounded-2xl p-8 text-center space-y-4">
									<h2 className="text-xl text-white font-semibold">
										Réservez votre RDV éditorial
									</h2>

									<p className="text-white/60 text-sm max-w-md mx-auto">
										Co-créons votre ligne éditoriale et définissons les
										thématiques de vos vidéos pour maximiser leur impact.
									</p>

									<div className="flex items-center justify-center gap-2">
										<Clock className="h-4 w-4 text-white" />
										<span className="text-white/70 text-xs">
											{preparationDuration} minutes • Dans les 15 jours avant le
											tournage
										</span>
									</div>
								</div>

								{/* Dates avec créneaux */}
								{availableDates.map((date) => {
									const dateKey = getDateKey(date);
									const slots = dateSlots.get(dateKey);

									if (!slots || slots.length === 0) return null;

									return (
										<div key={dateKey} className="space-y-4">
											{/* Date header */}
											<div className="flex items-center gap-3">
												<span className="text-white/70 text-sm font-medium capitalize">
													{formatDateHeader(date)}
												</span>
												<Separator className="bg-white/10 flex-1" />
											</div>

											{/* Scroll horizontal */}
											<div
												className="overflow-x-auto scrollbar-hide py-2"
												style={{
													touchAction: "pan-x",
													WebkitOverflowScrolling: "touch",
												}}
											>
												<div className="flex gap-3 min-w-max">
													{slots.map((slot, index) => {
														const isSelected =
															selectedSlot?.start === slot.start;

														return (
															<button
																key={index}
																onClick={() => handleSlotSelect(slot, date)}
																className={cn(
																	"flex-shrink-0 px-6 py-4 rounded-xl transition-all",
																	isSelected
																		? "ring-2 ring-white/20 bg-white/10 text-white"
																		: "glass text-white/70 active:scale-95",
																)}
															>
																{formatTimeRange(slot.start, slot.end)}
															</button>
														);
													})}
												</div>
											</div>
										</div>
									);
								})}

								{/* Bouton conciergerie */}
								<div className="space-y-4 mt-6">
									<p className="text-white/60 text-xs">
										Vous ne trouvez pas de créneaux ?
									</p>
									<button
										onClick={handleContactConcierge}
										className="glass text-white/70 rounded-xl px-6 py-4 active:scale-95 transition-all inline-flex items-center gap-3"
									>
										<MessageCircle className="h-5 w-5" />
										<span className="text-sm">Solliciter la conciergerie</span>
									</button>
								</div>
							</>
						)}
					</div>
				</div>

				{/* Footer sticky */}
				{selectedSlot && selectedDate && (
					<div className="glass border-t border-white/10 p-6 pb-8">
						<div className="max-w-xl mx-auto space-y-4">
							{/* Summary */}
							<div className="glass rounded-xl p-4">
								<div className="flex items-center justify-between">
									<div className="text-left">
										<p className="text-white/50 text-xs mb-1">
											Créneau sélectionné
										</p>
										<p className="text-white text-sm">
											{selectedDate.toLocaleDateString("fr-FR")}
										</p>
										<p className="text-white text-sm font-medium mt-1">
											{formatTimeRange(selectedSlot.start, selectedSlot.end)}
										</p>
									</div>
									<div className="text-right">
										<p className="text-white/50 text-xs mb-1">Durée</p>
										<p className="text-white text-sm">
											{preparationDuration} minutes
										</p>
									</div>
								</div>
							</div>

							{/* Bouton confirm */}
							<button
								onClick={handleConfirmBooking}
								disabled={isBooking}
								className="w-full rounded-2xl px-8 py-4 bg-white/10 hover:bg-white/15 text-white active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isBooking ? (
									<span className="flex items-center justify-center gap-3">
										<Loader2 className="h-5 w-5 animate-spin" />
										Confirmation en cours...
									</span>
								) : (
									"Réserver le RDV Edito"
								)}
							</button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
